import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

export type Tone = 'blue' | 'green' | 'orange' | 'violet' | 'gray' | 'red'

const TONE_CLASS: Record<Tone, string> = {
  blue: 'tone-blue',
  green: 'tone-green',
  orange: 'tone-orange',
  violet: 'tone-violet',
  gray: 'tone-gray',
  red: 'tone-red',
}

export function toneClass(tone: Tone): string {
  return TONE_CLASS[tone]
}

/**
 * Ô chỉ số pastel: nhãn, con số, một dòng ngữ cảnh và (tùy chọn) một biểu đồ
 * nhỏ bên dưới. Màu ô cố định theo LOẠI số liệu (doanh số luôn xanh, tiền chờ
 * luôn cam…), không theo thứ tự đứng.
 *
 * Con số lớn dùng chữ số tỷ lệ thường (không tabular) — ở cỡ lớn, tabular làm
 * số "1" trông lỏng.
 */
export function KpiTile({
  tone,
  icon: Icon,
  label,
  value,
  hint,
  loading,
  error,
  to,
  children,
  className,
}: {
  tone: Tone
  icon: LucideIcon
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  loading?: boolean
  /** API lỗi: hiện "—" và nói rõ không tải được, không hiện số 0. */
  error?: boolean
  /** Có `to` thì cả ô bấm được, dẫn tới trang chi tiết. */
  to?: string
  children?: ReactNode
  className?: string
}) {
  const body = (
    <>
      <div className="flex items-center gap-2.5">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-raise text-toneFg">
          <Icon className="size-4" aria-hidden />
        </span>
        <span className="min-w-0 text-[13px] font-medium leading-tight">{label}</span>
      </div>
      <div className="mt-3 truncate text-[22px] font-bold leading-tight tracking-tight mobile:text-lg">
        {loading ? <Skeleton className="h-7 w-32 bg-raise/70" /> : error ? '—' : value}
      </div>
      {error ? (
        <div className="mt-1 truncate text-xs opacity-90">Không tải được số liệu</div>
      ) : (
        hint && <div className="mt-1 truncate text-xs opacity-90">{loading ? ' ' : hint}</div>
      )}
      {children && !error && <div className="mt-3">{children}</div>}
    </>
  )
  const cls = cn(
    toneClass(tone),
    'flex min-w-0 flex-col rounded-xl bg-toneBg p-4 text-toneFg mobile:p-3',
    to && 'motion-state hover:brightness-[.97] focus-visible:ring-2 focus-visible:ring-toneFg',
    className,
  )
  return to ? (
    <Link to={to} className={cls} data-slot="kpi">
      {body}
    </Link>
  ) : (
    <div className={cls} data-slot="kpi">
      {body}
    </div>
  )
}

/**
 * Nút thao tác nhanh: ô icon pastel + tên việc + một dòng mô tả. `badge` là
 * số việc đang chờ (ví dụ số đợt giải ngân chờ duyệt).
 */
export function QuickAction({
  tone,
  icon: Icon,
  title,
  desc,
  to,
  badge,
}: {
  tone: Tone
  icon: LucideIcon
  title: string
  desc?: string
  to: string
  badge?: number
}) {
  return (
    <Link
      to={to}
      className={cn(
        toneClass(tone),
        'motion-state relative flex min-w-0 items-center gap-3 rounded-xl bg-raise p-3.5 shadow-card hover:-translate-y-px hover:shadow-pop',
        'mobile:flex-col mobile:items-start mobile:gap-2 mobile:p-3',
      )}
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-toneBg text-toneFg">
        <Icon className="size-5" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-ink">{title}</span>
        {desc && <span className="block truncate text-xs text-muted mobile:hidden">{desc}</span>}
      </span>
      {!!badge && badge > 0 && (
        <span className="absolute right-2.5 top-2.5 grid h-5 min-w-5 place-items-center rounded-full bg-danger px-1.5 text-[11px] font-bold text-raise">
          {badge}
        </span>
      )}
    </Link>
  )
}
